import { useState } from 'react';
import { useTrades, useCreateTrade } from '../hooks/useTrades';
import { TradeCard } from '../components/trade/TradeCard';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { useAuth } from '../context/AuthContext';
import type { TradeStatus } from '../types';

const statusFilters: Array<{ value: TradeStatus | 'ALL'; label: string }> = [
  { value: 'ALL',       label: 'Todos' },
  { value: 'PENDING',   label: 'Pendientes' },
  { value: 'ACCEPTED',  label: 'Aceptados' },
  { value: 'COMPLETED', label: 'Completados' },
  { value: 'REJECTED',  label: 'Rechazados' },
  { value: 'CANCELLED', label: 'Cancelados' },
  { value: 'EXPIRED',   label: 'Expirados' },
];

const filterActive: Record<TradeStatus | 'ALL', string> = {
  ALL:       'bg-night text-white border-night',
  PENDING:   'bg-amber-400 text-white border-amber-400',
  ACCEPTED:  'bg-blue-500 text-white border-blue-500',
  COMPLETED: 'bg-green-500 text-white border-green-500',
  REJECTED:  'bg-red-500 text-white border-red-500',
  CANCELLED: 'bg-gray-400 text-white border-gray-400',
  EXPIRED:   'bg-gray-400 text-white border-gray-400',
};

export default function TradesPage() {
  const { data: trades, isLoading } = useTrades();
  const createTrade = useCreateTrade();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<TradeStatus | 'ALL'>('ALL');

  const [form, setForm] = useState({
    targetUserId: '',
    offeredStickerId: '',
    requestedStickerId: '',
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createTrade.mutate(
      {
        targetUserId: form.targetUserId,
        offeredStickers: [form.offeredStickerId],
        requestedStickers: [form.requestedStickerId],
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setForm({ targetUserId: '', offeredStickerId: '', requestedStickerId: '' });
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  const allTrades = trades ?? [];
  const filtered  = filter === 'ALL' ? allTrades : allTrades.filter((t) => t.status === filter);
  const pending   = allTrades.filter((t) => t.status === 'PENDING').length;

  // Incoming = pending trades where current user is the target
  const incoming = allTrades.filter(
    (t) => (t.status === 'PENDING' || t.status === 'ACCEPTED') && t.targetId === user?.userId
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-night" style={{ fontFamily: '"Fredoka One", cursive' }}>
            Intercambios
          </h1>
          <p className="text-sm text-gray-400 font-semibold mt-0.5">
            {allTrades.length} total
            {pending > 0 && (
              <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">
                {pending} pendiente{pending > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} variant={showForm ? 'secondary' : 'primary'}>
          {showForm ? '✕ Cancelar' : '+ Nuevo intercambio'}
        </Button>
      </div>

      {/* New trade form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-5 shadow-card border border-brand-100">
          <h3 className="font-bold text-gray-800 mb-4">Proponer intercambio</h3>
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                ID del usuario destino
              </label>
              <input
                placeholder="ej: abc123-def456-..."
                value={form.targetUserId}
                onChange={(e) => setForm({ ...form, targetUserId: e.target.value })}
                required
                className="input-field"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                  # Figurita que ofreces
                </label>
                <input
                  placeholder="ej: 42"
                  value={form.offeredStickerId}
                  onChange={(e) => setForm({ ...form, offeredStickerId: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                  # Figurita que pides
                </label>
                <input
                  placeholder="ej: 107"
                  value={form.requestedStickerId}
                  onChange={(e) => setForm({ ...form, requestedStickerId: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
            </div>
            {createTrade.isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                Error al crear el intercambio. Verifica que las figuritas estén disponibles.
              </div>
            )}
            <Button type="submit" loading={createTrade.isPending} className="self-start">
              Enviar propuesta
            </Button>
          </form>
        </div>
      )}

      {/* Incoming proposals banner */}
      {incoming.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">📥</span>
            <h2 className="font-bold text-amber-800">
              {incoming.length} propuesta{incoming.length > 1 ? 's' : ''} recibida{incoming.length > 1 ? 's' : ''}
            </h2>
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {incoming.map((trade) => (
              <TradeCard key={trade.tradeId} trade={trade} />
            ))}
          </div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-1.5 flex-wrap">
        {statusFilters.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={[
              'px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all',
              filter === s.value
                ? filterActive[s.value]
                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700',
            ].join(' ')}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">🤝</div>
          <p className="font-semibold">
            {allTrades.length === 0
              ? 'Aún no tienes intercambios. ¡Propone uno!'
              : 'No hay intercambios con este filtro'}
          </p>
        </div>
      )}

      {/* Trade grid */}
      {filtered.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {filtered.map((trade) => (
            <TradeCard key={trade.tradeId} trade={trade} />
          ))}
        </div>
      )}
    </div>
  );
}
